/**
 * Converts lastCall objects from the feed builder UI into algo-dsl JavaScript code.
 * Each converter turns one service's lastCall (endpoint + payload) into a readable algo-dsl chain.
 */


const CHAIN_INDENT = '\n  ';

const defaultCode = `// Use mbd here
// Available variables: 
//   mbd: the Studio instance
//   farcasterFid: the Farcaster FID
//   polymarketWallet: the Polymarket wallet address
mbd.log('Hello from the feed builder');
mbd.log('farcasterFid='+farcasterFid)
mbd.log('polymarketWallet='+polymarketWallet)
mbd.forUser('polymarket-wallets', polymarketWallet);
candidates = await mbd.search()
  .index('polymarket-items')
  .execute();
mbd.addCandidates(candidates);
features = await mbd.features()
  .execute();
mbd.addFeatures(features);
scores = await mbd.scoring()
  .model('/scoring/ranking_model/polymarket-rerank-v1')
  .execute();
mbd.addScores(scores, 'score_polymkt1');
ranking = await mbd.ranking()
  .sortBy('score_polymkt1')
  .execute();
mbd.addRanking(ranking);
mbd.show();
`;


/**
 * Format a value as a JavaScript literal string for generated code (safe for embedding).
 * @param {*} val
 * @returns {string}
 */
function formatLiteral(val) {
  if (val === null || val === undefined) return 'null';
  if (typeof val === 'number') return String(val);
  if (typeof val === 'boolean') return val ? 'true' : 'false';
  if (typeof val === 'string') return JSON.stringify(val);
  if (Array.isArray(val)) return '[' + val.map((v) => formatLiteral(v)).join(', ') + ']';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

/**
 * Convert a single filter object (from API payload) to an algo-dsl method call string.
 * @param {object} f - Filter object with filter, field, value, etc.
 * @param {string} indent - Optional indent for multi-line
 * @returns {string} e.g. ".term('lang', \"en\")"
 */
function filterToSdkCall(f) {
  if (!f || typeof f.filter !== 'string') return '';
  const field = typeof f.field === 'string' ? f.field : '';
  const boost = f.boost != null ? `, ${formatLiteral(f.boost)}` : '';
  switch (f.filter) {
    case 'term':
      return `.term(${formatLiteral(field)}, ${formatLiteral(f.value)}${boost})`;
    case 'terms':
      return `.terms(${formatLiteral(field)}, ${formatLiteral(f.value)}${boost})`;
    case 'numeric':
      return `.numeric(${formatLiteral(field)}, ${formatLiteral(f.operator)}, ${formatLiteral(f.value)}${boost})`;
    case 'date': {
      const v = f.value || {};
      const from = v.date_from != null ? formatLiteral(v.date_from) : 'null';
      const to = v.date_to != null ? formatLiteral(v.date_to) : 'null';
      return `.date(${formatLiteral(field)}, ${from}, ${to}${boost})`;
    }
    case 'match':
      return `.match(${formatLiteral(field)}, ${formatLiteral(f.value)}${boost})`;
    case 'geo':
      return `.geo(${formatLiteral(field)}, ${formatLiteral(f.value)}${boost})`;
    case 'is_null':
      return `.isNull(${formatLiteral(field)}${boost})`;
    case 'not_null':
      return `.notNull(${formatLiteral(field)}${boost})`;
    case 'custom':
      return `.custom(${formatLiteral(field)}, ${formatLiteral(f.value)}${boost})`;
    case 'group_boost': {
      const li = f.lookup_index != null ? formatLiteral(f.lookup_index) : 'null';
      const grp = f.group != null ? formatLiteral(f.group) : 'null';
      const minB = f.min_boost != null ? formatLiteral(f.min_boost) : 'null';
      const maxB = f.max_boost != null ? formatLiteral(f.max_boost) : 'null';
      const n = f.n != null ? formatLiteral(f.n) : 'null';
      return `.groupBoost(${li}, ${formatLiteral(field)}, ${formatLiteral(f.value)}, ${grp}, ${minB}, ${maxB}, ${n})`;
    }
    case 'terms_lookup': {
      const li = f.lookup_index != null ? formatLiteral(f.lookup_index) : 'null';
      const path = f.path != null ? formatLiteral(f.path) : 'null';
      return `.termsLookup(${li}, ${formatLiteral(field)}, ${formatLiteral(f.value)}, ${path}${boost})`;
    }
    case 'console_account': {
      const path = f.path != null ? formatLiteral(f.path) : 'null';
      return `.consoleAccount(${formatLiteral(field)}, ${formatLiteral(f.value)}, ${path}${boost})`;
    }
    default:
      return '';
  }
}

/**
 * Convert search lastCall to mbd.search() code.
 * @param {{ endpoint: string, payload: object } | null} searchLastCall
 * @returns {string} e.g. "mbd.search().index('polymarket-items').include()...execute()"
 */
export function convertSearchCall(searchLastCall) {
  if (!searchLastCall?.payload) return '';
  const p = searchLastCall.payload;
  const endpoint = searchLastCall.endpoint || '';
  const parts = ['mbd.search()'];

  if (p.index) parts.push(`.index(${formatLiteral(p.index)})`);
  if (p.size != null && p.size !== 100) parts.push(`.size(${p.size})`);
  if (p.include_vector) parts.push('.includeVectors(true)');
  if (p.only_ids) parts.push('.onlyIds(true)');
  if (Array.isArray(p.select_fields) && p.select_fields.length > 0) {
    parts.push(`.selectFields(${formatLiteral(p.select_fields)})`);
  }

  if (endpoint === '/search/es_query') {
    if (p.query != null) parts.push(`.esQuery(${formatLiteral(p.query)})`);
  } else if (endpoint === '/search/semantic') {
    if (typeof p.text === 'string' && p.text.length > 0) parts.push(`.text(${formatLiteral(p.text)})`);
    if (Array.isArray(p.vector) && p.vector.length > 0) parts.push(`.vector(${formatLiteral(p.vector)})`);
  } else {
    // filter_and_sort or boost
    const includeFilters = Array.isArray(p.include) ? p.include : [];
    const excludeFilters = Array.isArray(p.exclude) ? p.exclude : [];
    const boostFilters = Array.isArray(p.boost) ? p.boost : [];

    if (includeFilters.length > 0) {
      parts.push('.include()');
      includeFilters.forEach((f) => {
        const call = filterToSdkCall(f);
        if (call) parts.push(call);
      });
    }
    if (excludeFilters.length > 0) {
      parts.push('.exclude()');
      excludeFilters.forEach((f) => {
        const call = filterToSdkCall(f);
        if (call) parts.push(call);
      });
    }
    if (boostFilters.length > 0) {
      parts.push('.boost()');
      boostFilters.forEach((f) => {
        const call = filterToSdkCall(f);
        if (call) parts.push(call);
      });
    }
    if (endpoint === '/search/filter_and_sort' && p.sort_by?.field) {
      const dir = p.sort_by.order === 'asc' ? 'asc' : 'desc';
      parts.push(`.sortBy(${formatLiteral(p.sort_by.field)}, '${dir}')`);
    }
  }

  parts.push('.execute()');
  return parts.join(CHAIN_INDENT);
}

/**
 * Convert features lastCall to mbd.features() code.
 * User and items are provided by mbd.forUser() and mbd.addCandidates() in the main flow.
 * @param {{ endpoint: string, payload: object } | null} featuresLastCall
 * @returns {string} e.g. "mbd.features('v1').execute()"
 */
export function convertFeaturesCall(featuresLastCall) {
  if (!featuresLastCall?.endpoint) return '';
  const match = featuresLastCall.endpoint.match(/^\/features\/(.+)$/);
  const version = match ? match[1] : 'v1';
  return `mbd.features(${formatLiteral(version)})${CHAIN_INDENT}.execute()`;
}

/**
 * Convert hydration lastCall to mbd.hydration() code.
 * Index and item_ids come from candidates; targets come from the payload's
 * `hydrate` block (bare target names). When the payload omits `hydrate`,
 * server defaults apply — the SDK call still works with no .target() chain.
 * @param {{ endpoint: string, payload: object } | null} hydrationLastCall
 * @returns {string} e.g. "mbd.hydration().target('user_trades', {...}).execute()"
 */
export function convertHydrationCall(hydrationLastCall) {
  if (!hydrationLastCall?.payload) return '';
  const p = hydrationLastCall.payload;
  const parts = ['mbd.hydration()'];
  const hydrate = p.hydrate;
  if (hydrate && typeof hydrate === 'object') {
    for (const [target, spec] of Object.entries(hydrate)) {
      parts.push(`.target(${formatLiteral(target)}, ${formatLiteral(spec)})`);
    }
  }
  if (p.drop_empty_hits) parts.push('.dropEmptyHits(true)');
  parts.push('.execute()');
  return parts.join(CHAIN_INDENT);
}

/**
 * Convert scoring lastCall to mbd.scoring() code.
 * User and item IDs come from mbd.forUser() and candidates in the main flow.
 * @param {{ endpoint: string, payload: object } | null} scoringLastCall
 * @returns {{ code: string, scoreKey: string }} e.g. { code: "mbd.scoring().model(...).execute()", scoreKey: "score_polymkt1" }
 */
export function convertScoringCall(scoringLastCall) {
  if (!scoringLastCall?.endpoint) return { code: '', scoreKey: '' };
  const endpoint = scoringLastCall.endpoint;
  const modelPath = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const scoreKey =
    endpoint
      .replace(/^\/scoring\//, '')
      .replace(/\//g, '_')
      .replace(/[^a-zA-Z0-9_]/g, '_') || 'score';
  const code = `mbd.scoring()${CHAIN_INDENT}.model(${formatLiteral(modelPath)})${CHAIN_INDENT}.execute()`;
  return { code, scoreKey };
}

/**
 * Convert ranking lastCall to mbd.ranking() code.
 * Candidates are already on the studio; sort/diversity/limits come from payload.
 * @param {{ endpoint: string, payload: object } | null} rankingLastCall
 * @returns {string} e.g. "mbd.ranking().sortBy('score_polymkt1', 'desc').execute()"
 */
export function convertRankingCall(rankingLastCall) {
  if (!rankingLastCall?.payload) return '';
  const p = rankingLastCall.payload;
  const sort = p.sort;
  const hasSort =
    (sort?.method === 'sort' && sort.params?.fields?.length > 0) ||
    (sort?.method === 'linear' && Array.isArray(sort.params) && sort.params.length > 0) ||
    (sort?.method === 'mix' && Array.isArray(sort.params) && sort.params.length > 0);
  if (!hasSort) return '';

  const parts = ['mbd.ranking()'];

  if (sort?.method === 'sort' && sort.params?.fields?.length > 0) {
    const fields = sort.params.fields;
    const directions = sort.params.direction || sort.params.directions || [];
    const d0 = directions[0] === 'asc' ? 'asc' : 'desc';
    if (fields.length === 1) {
      parts.push(`.sortBy(${formatLiteral(fields[0])}, '${d0}')`);
    } else if (fields.length >= 2) {
      const d1 = directions[1] === 'asc' ? 'asc' : 'desc';
      parts.push(`.sortBy(${formatLiteral(fields[0])}, '${d0}', ${formatLiteral(fields[1])}, '${d1}')`);
    }
  } else if (sort?.method === 'linear' && Array.isArray(sort.params) && sort.params.length > 0) {
    parts.push(".sortingMethod('linear')");
    sort.params.forEach(({ field, weight }) => {
      if (field != null) parts.push(`.weight(${formatLiteral(field)}, ${Number(weight)})`);
    });
  } else if (sort?.method === 'mix' && Array.isArray(sort.params) && sort.params.length > 0) {
    parts.push(".sortingMethod('mix')");
    sort.params.forEach(({ field, direction, percentage }) => {
      if (field != null) parts.push(`.mix(${formatLiteral(field)}, '${direction === 'asc' ? 'asc' : 'desc'}', ${Number(percentage)})`);
    });
  }

  const diversity = p.diversity;
  if (diversity?.method === 'fields' && diversity.params?.fields?.length > 0) {
    parts.push(".diversity('fields')");
    diversity.params.fields.forEach((field) => {
      parts.push(`.fields(${formatLiteral(field)})`);
    });
  } else if (diversity?.method === 'semantic') {
    parts.push(".diversity('semantic')");
    const lambda = diversity.params?.lambda ?? 0.5;
    const horizon = diversity.params?.horizon ?? 20;
    parts.push(`.lambda(${Number(lambda)})`);
    parts.push(`.horizon(${Number(horizon)})`);
  }

  const limits = p.limits_by_field;
  if (limits?.every_n != null && Array.isArray(limits.rules) && limits.rules.length > 0) {
    parts.push('.limitByField()');
    parts.push(`.every(${Number(limits.every_n)})`);
    limits.rules.forEach(({ field, limit }) => {
      if (field != null) parts.push(`.limit(${formatLiteral(field)}, ${Number(limit)})`);
    });
  }

  parts.push('.execute()');
  return parts.join(CHAIN_INDENT);
}

/**
 * Derive user context (index, id) from the last calls for forUser().
 * Prefer features payload (user), then scoring (user_id only - we don't have index), then leave empty.
 * @param {{ endpoint: string, payload: object } | null} featuresLastCall
 * @param {{ endpoint: string, payload: object } | null} scoringLastCall
 * @returns {{ index: string, id: string } | null}
 */
function getUserFromLastCalls(featuresLastCall, scoringLastCall) {
  if (featuresLastCall?.payload?.user?.index && featuresLastCall?.payload?.user?.id) {
    return {
      index: featuresLastCall.payload.user.index,
      id: featuresLastCall.payload.user.id,
    };
  }
  if (scoringLastCall?.payload?.user_id) {
    return { index: '', id: scoringLastCall.payload.user_id };
  }
  return null;
}

/**
 * Generate full algo-dsl program from last call objects.
 * @param {{ endpoint: string, payload: object } | null} searchCall
 * @param {{ endpoint: string, payload: object } | null} featuresCall
 * @param {{ endpoint: string, payload: object } | null} scoringCall
 * @param {{ endpoint: string, payload: object } | null} rankingCall
 * @returns {string} 
 */
export function generateCode(searchCall, featuresCall, scoringCall, rankingCall, hydrationCall) {
  if (!searchCall) return defaultCode;
  const lines = [];
  lines.push('version = mbd.version();')
  lines.push("mbd.log('Using Algorithms DSL version '+version);");

  const user = getUserFromLastCalls(featuresCall, scoringCall);
  if (user && user.index && user.id) {
    // Use params when available so the run inputs control the user
    const userArg =
      user.index === 'polymarket-wallets' ? 'polymarketWallet' :
      user.index === 'farcaster-profiles' ? 'farcasterFid' :
      formatLiteral(user.id);
    lines.push(`mbd.forUser(${formatLiteral(user.index)}, ${userArg});`);
  } else if (user && user.id) {
    lines.push(`// User ID from scoring (index not set): ${formatLiteral(user.id)}`);
  }

  const searchCode = convertSearchCall(searchCall);
  if (searchCode) {
    lines.push('');
    lines.push(`candidates = await ${searchCode}`);
    lines.push('mbd.addCandidates(candidates);');
  }

  const hydrationCode = convertHydrationCall(hydrationCall);
  if (hydrationCode) {
    lines.push('');
    lines.push(`hydration = await ${hydrationCode}`);
    lines.push('mbd.addHydration(hydration);');
  }

  const featuresCode = convertFeaturesCall(featuresCall);
  if (featuresCode) {
    lines.push('');
    lines.push(`features = await ${featuresCode}`);
    lines.push('mbd.addFeatures(features);');
  }

  const { code: scoringCode, scoreKey } = convertScoringCall(scoringCall);
  if (scoringCode && scoreKey) {
    lines.push('');
    lines.push(`scores = await ${scoringCode}`);
    lines.push(`mbd.addScores(scores, ${formatLiteral(scoreKey)});`);
  }

  const rankingCode = convertRankingCall(rankingCall);
  if (rankingCode) {
    lines.push('');
    lines.push(`ranking = await ${rankingCode}`);
    lines.push('mbd.addRanking(ranking);');
  }
  
  lines.push('');
  lines.push('mbd.show();');
  return lines.join('\n');
}
